/**
 * A purchase slip: what was bought, how many packs, at what price per pack.
 * Saving puts it on the Store shelf and remembers the price on each item.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTime, today } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { ItemPicker, packs } from './ItemPicker.jsx';
import { DateFilter, useDateFilter } from './DateFilter.jsx';
import { PurchaseSlipSheet } from './PurchaseSlip.jsx';

export function BuyTab({ items, admin = false }) {
  const qc = useQueryClient();
  const dates = useDateFilter();
  const recent = useQuery({ queryKey: ['store', 'purchases', dates.query], queryFn: () => api.get(`/store/purchases?limit=${dates.range ? 500 : 50}${dates.query}`) });
  const [supplier, setSupplier] = useState('Local market');
  const [note, setNote] = useState('');
  // The day it was bought: today, unless the slip is being entered late.
  const [date, setDate] = useState(today());
  const [printing, setPrinting] = useState(null);
  const [lines, setLines] = useState([]);
  const [saved, setSaved] = useState(null);
  // An admin putting a saved slip right: the slip being edited, loaded into the form above.
  const [editing, setEditing] = useState(null);

  const startEdit = (p) => {
    const byId = new Map(items.map((i) => [i.id, i]));
    setEditing({ id: p.id, number: p.grn_number, wasCredit: Boolean(p.onCredit) });
    setSupplier(p.supplier_name || 'Local market');
    setNote(p.notes || '');
    setDate(String(p.received_date || today()).slice(0, 10));
    setLines(
      (p.items ?? []).map((l) => {
        const it = byId.get(l.ingredientId);
        return { ingredientId: l.ingredientId, name: l.name, packName: l.packName, packSize: it?.packSize ?? 1, unit: it?.unit ?? '', packs: String(l.packs), ratePerPack: String(l.ratePerPack) };
      })
    );
    setSaved(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const stopEdit = () => {
    setEditing(null);
    setLines([]);
    setNote('');
    setDate(today());
    setSupplier('Local market');
  };

  const add = (item) => {
    if (lines.some((l) => l.ingredientId === item.id)) return;
    setLines([...lines, { ingredientId: item.id, name: item.name, packName: item.packName, packSize: item.packSize, unit: item.unit, packs: '', ratePerPack: item.packPrice || '' }]);
  };
  const edit = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const total = lines.reduce((s, l) => s + (Number(l.packs) || 0) * (Number(l.ratePerPack) || 0), 0);

  const save = useMutation({
    mutationFn: (payment) => {
      const body = { supplierName: supplier, note, payment, date, lines: lines.map((l) => ({ ingredientId: l.ingredientId, packs: Number(l.packs), ratePerPack: Number(l.ratePerPack) })) };
      return editing ? api.put(`/store/purchases/${editing.id}`, body).then((r) => ({ ...r, edited: true })) : api.post('/store/purchases', body);
    },
    onSuccess: (res) => {
      setSaved(res);
      setEditing(null);
      setSupplier('Local market');
      setLines([]);
      setNote('');
      setDate(today());
      qc.invalidateQueries({ queryKey: ['store'] });
    },
  });
  const ready = lines.length > 0 && lines.every((l) => Number(l.packs) > 0 && Number(l.ratePerPack) >= 0 && l.ratePerPack !== '');

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <section className="rounded-xl border border-rule bg-surface p-4 lg:col-span-3">
        {editing ? (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2">
            <span className="text-sm text-ink">
              Editing slip <span className="font-semibold">{editing.number}</span>. Saving replaces it: the shelf, the books and the supplier&rsquo;s account follow.
            </span>
            <Button size="sm" variant="secondary" onClick={stopEdit}>
              Cancel edit
            </Button>
          </div>
        ) : (
          <h2 className="mb-3 text-sm font-medium text-ink-2">New purchase slip</h2>
        )}
        <div className="mb-3 grid gap-2 sm:grid-cols-3">
          <label className="text-sm text-ink-2">
            Bought on
            <input type="date" value={date} max={today()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Bought on" className="mt-1 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
          </label>
          <label className="text-sm text-ink-2">
            Bought from
            <input list="store-suppliers" value={supplier} onChange={(e) => setSupplier(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
            <datalist id="store-suppliers">
              {(recent.data?.suppliers ?? []).map((s) => (
                <option key={s.id} value={s.name} />
              ))}
            </datalist>
          </label>
          <label className="text-sm text-ink-2">
            Note
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="slip number, who paid…" className="mt-1 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
          </label>
        </div>
        <ItemPicker items={items} onPick={add} placeholder="Add an item to the slip…" />
        {lines.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-3">
                <th className="py-1 font-medium">Item</th>
                <th className="py-1 text-right font-medium">Packs</th>
                <th className="py-1 text-right font-medium">Rs / pack</th>
                <th className="py-1 text-right font-medium">Line</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.ingredientId} className="border-t border-rule">
                  <td className="py-2 text-ink">
                    {l.name}
                    <div className="text-xs text-ink-3">
                      {l.packName}
                      {l.packSize !== 1 ? ` = ${packs(l.packSize)} ${l.unit}` : ''}
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    <input type="number" step="any" min="0" value={l.packs} onChange={(e) => edit(i, { packs: e.target.value })} aria-label={`Packs of ${l.name}`} className="h-10 w-20 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm text-ink" />
                  </td>
                  <td className="py-2 text-right">
                    <input type="number" step="any" min="0" value={l.ratePerPack} onChange={(e) => edit(i, { ratePerPack: e.target.value })} aria-label={`Price per pack of ${l.name}`} className="h-10 w-24 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm text-ink" />
                  </td>
                  <td className="py-2 text-right tabular-nums text-ink-2">{formatPKR((Number(l.packs) || 0) * (Number(l.ratePerPack) || 0))}</td>
                  <td className="py-2 text-right">
                    <button type="button" onClick={() => setLines(lines.filter((_, k) => k !== i))} aria-label={`Remove ${l.name}`} className="text-xs text-ink-3 hover:text-bad">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-ink-2">
            Total <span className="text-lg font-semibold tabular-nums text-ink">{formatPKR(total)}</span>
          </span>
          <div className="flex gap-2">
            {/* Bought on credit: the slip goes on the supplier's account for the owner to settle. */}
            <Button variant="secondary" onClick={() => save.mutate('credit')} disabled={!ready} busy={save.isPending && save.variables === 'credit'}>
              {editing ? 'Save as credit' : 'Buy on credit'}
            </Button>
            <Button onClick={() => save.mutate('paid')} disabled={!ready} busy={save.isPending && save.variables === 'paid'}>
              {editing ? 'Save as paid' : 'Save slip (paid)'}
            </Button>
          </div>
        </div>
        {save.error && (
          <p role="alert" className="mt-2 text-sm text-bad">
            {save.error.message}
          </p>
        )}
        {saved && (
          <p className="mt-2 text-sm text-good">
            {saved.edited ? `Slip ${saved.number} corrected` : `Saved as ${saved.number}`} — {formatPKR(saved.total)} onto the store shelf{saved.onCredit || saved.payment === 'credit' ? ', on credit' : ''}{saved.date && saved.date !== today() ? `, dated ${saved.date}` : ''}.
          </p>
        )}
      </section>

      <section className="rounded-xl border border-rule bg-surface p-4 lg:col-span-2">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-ink-2">{dates.range ? 'Slips on those days' : 'Recent slips'}</h2>
          <DateFilter range={dates.range} onChange={dates.setRange} />
        </div>
        {dates.range && recent.data && (
          <p className="mb-2 text-xs text-ink-3">
            {recent.data.purchases.length} slip{recent.data.purchases.length === 1 ? '' : 's'} · {formatPKR(recent.data.purchases.reduce((t, p) => t + (Number(p.total) || 0), 0))}
          </p>
        )}
        <ul className="space-y-1.5 text-sm">
          {(recent.data?.purchases ?? []).map((p) => (
            <li key={p.id} className="rounded-lg border border-rule px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-ink">
                  {p.grn_number} <span className="text-ink-3">· {p.supplier_name}</span>
                  {p.onCredit && (
                    <span className={`ml-2 rounded-full px-1.5 text-xs ${p.creditLeft > 0 ? 'bg-warn/15 text-warn' : 'bg-good/15 text-good'}`}>
                      {p.creditLeft > 0 ? `on credit · ${formatPKR(p.creditLeft)} left` : 'credit, settled'}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums text-ink-2">{formatPKR(p.total)}</span>
                  <Button size="sm" variant="secondary" onClick={() => setPrinting(p)}>
                    Print
                  </Button>
                  {admin && (
                    <Button size="sm" variant="secondary" onClick={() => startEdit(p)} aria-label={`Edit slip ${p.grn_number}`}>
                      Edit
                    </Button>
                  )}
                </span>
              </div>
              <div className="text-xs text-ink-3">
                {formatServerTime(p.created_at)} · {p.entered_by}
                {p.notes ? ` · ${p.notes}` : ''}
              </div>
              <ul className="mt-1.5 space-y-0.5 border-t border-rule pt-1.5 text-xs text-ink-2">
                {(p.items ?? []).map((l) => (
                  <li key={l.ingredientId} className="flex justify-between gap-2">
                    <span>
                      {l.name} · {packs(l.packs)} × {l.packName}
                    </span>
                    <span className="tabular-nums">
                      @ {formatPKR(l.ratePerPack)} = {formatPKR(l.total)}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
          {recent.data && recent.data.purchases.length === 0 && <li className="text-ink-3">{dates.range ? 'No slips on those days.' : 'No slips yet.'}</li>}
        </ul>
      </section>
      {printing && <PurchaseSlipSheet slip={printing} onClose={() => setPrinting(null)} />}
    </div>
  );
}
